/**
 * Permission Provider - React Context for Permission Management
 * 
 * This provider manages permission state, real-time updates, and provides
 * permission checking functionality to React components throughout the app.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { invokeEdgeFunction } from '@/lib/supabase-functions-wrapper';
import { useAuth } from '@/context/auth-context';
import { useUserContext } from '@/hooks/use-user-context';
import { systemLogger } from '@/lib/logging';
import {
  PermissionRequirement,
  PermissionResult,
  ProjectMembership,
  CompanyContract,
  UserRole,
  ProjectPermission,
  PermissionAction,
  PermissionResource,
  PERMISSION_CONFIG
} from '../types';
import { permissionService } from '../services';
import { permissionIntegrity } from '../services/permission-integrity';
import { securePermissionCache } from '../services/secure-cache';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface PermissionContextType {
  // Core permission checking
  hasPermission: (_requirement: PermissionRequirement) => Promise<PermissionResult>;
  
  // Convenience methods
  canViewCompany: (_companyId: string) => Promise<boolean>;
  canEditCompany: (_companyId: string) => Promise<boolean>;
  canDeleteCompany: (_companyId: string) => Promise<boolean>;
  
  canCreateProject: () => Promise<boolean>;
  canViewProject: (_projectId: string) => Promise<boolean>;
  canEditProject: (_projectId: string, _projectStatus?: string) => Promise<boolean>;
  canDeleteProject: (_projectId: string) => Promise<boolean>;

  // Project-specific operations
  canCreateSubproject: (_projectId: string, _projectStatus?: string) => Promise<boolean>;
  canCreateProjectReport: (_projectId: string) => Promise<boolean>;
  canAcceptProject: (_projectId: string) => Promise<boolean>;
  canRejectProject: (_projectId: string) => Promise<boolean>;
  canChangeProjectStatus: (_projectId: string) => Promise<boolean>;

  canCreateUser: (_targetCompanyId?: string) => Promise<boolean>;
  canEditUser: (_targetUserId: string) => Promise<boolean>;
  canDeleteUser: (_targetUserId: string) => Promise<boolean>;

  // User info
  userId: string | null;
  userRole: UserRole;
  isSystemAdmin: boolean;
  isCompanyAdmin: boolean;
  
  // Project memberships (cached for performance)
  projectMemberships: ProjectMembership[];
  getUserProjectPermission: (_projectId: string) => ProjectPermission | null;
  
  // Cache management
  clearCache: () => void;
  refreshPermissions: () => Promise<void>;
  
  // Loading state
  isLoading: boolean;
  
  // Debug mode
  debugMode: boolean;
  setDebugMode: (_enabled: boolean) => void;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface PermissionProviderProps {
  children: ReactNode;
  debugMode?: boolean;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  children,
  debugMode = PERMISSION_CONFIG.DEBUG_MODE
}) => {
  const { user: _authUser } = useAuth();
  const userContext = useUserContext();
  const queryClient = useQueryClient();

  // State
  const [debugModeState, setDebugModeState] = useState(debugMode);

  // PERFORMANCE: Use React Query to deduplicate permission calls
  // Single fetch for ALL permission data, cached and shared across all components
  const { data: permissionsData, isLoading } = useQuery({
    queryKey: ['user-permissions', userContext?.id],
    queryFn: async () => {
      if (!userContext?.id) {
        return {
          project_memberships: [],
          company_contracts: []
        };
      }

      const { data, error } = await invokeEdgeFunction('get-user-permissions');

      if (error) {
        systemLogger.error('Failed operation:', error);
        return {
          project_memberships: [],
          company_contracts: []
        };
      }

      const typedData = data as {
        project_memberships?: ProjectMembership[];
        company_contracts?: CompanyContract[];
      };

      return {
        project_memberships: typedData?.project_memberships || [],
        company_contracts: typedData?.company_contracts || []
      };
    },
    enabled: !!userContext?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const projectMemberships = useMemo(
    () => permissionsData?.project_memberships || [],
    [permissionsData]
  );

  const companyContracts = useMemo(
    () => permissionsData?.company_contracts || [],
    [permissionsData]
  );

  // ============================================================================
  // PERMISSION CHECKING METHODS
  // ============================================================================

  const hasPermission = useCallback(async (requirement: PermissionRequirement): Promise<PermissionResult> => {
    // DEBUG: Log permission check attempt
    systemLogger.debug('[PERMISSION-DEBUG] Permission check requested:', {
      requirement,
      userContext: userContext ? {
        id: userContext.id,
        email: userContext.email,
        app_role: userContext.app_role,
        is_system_admin: userContext.is_system_admin,
        company_id: userContext.company_id,
        profile: userContext.profile ? {
          role: userContext.profile.role,
          company_id: userContext.profile.company_id,
          company_type: userContext.profile.company?.type
        } : null
      } : null
    });

    if (!userContext) {
      systemLogger.debug('[PERMISSION-DEBUG] Permission denied: User not authenticated');
      return {
        allowed: false,
        reason: 'User not authenticated'
      };
    }

    try {
      // PERFORMANCE: Use React Query cached data instead of fetching again
      // This eliminates ~12 duplicate edge function calls
      const result = await permissionService.evaluateWithProvidedData(
        userContext,
        requirement,
        projectMemberships,
        companyContracts as CompanyContract[]
      );

      systemLogger.debug('[PERMISSION-DEBUG] Permission check result:', {
        requirement: `${requirement.action}.${requirement.resource}`,
        allowed: result.allowed,
        reason: result.reason
      });
      return result;
    } catch (_error) {
      systemLogger.debug('[PERMISSION-DEBUG] Permission check failed with error:', { error: _error });
      systemLogger.error('Failed operation:', _error);
      return {
        allowed: false,
        reason: 'Permission check failed'
      };
    }
  }, [userContext, projectMemberships, companyContracts]);

  // Company permissions
  // PERFORMANCE: Use cached hasPermission() instead of calling service directly
  const canViewCompany = useCallback(async (companyId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.VIEW,
      resource: PermissionResource.COMPANY,
      resourceId: companyId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canEditCompany = useCallback(async (companyId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.EDIT,
      resource: PermissionResource.COMPANY,
      resourceId: companyId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canDeleteCompany = useCallback(async (companyId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.DELETE,
      resource: PermissionResource.COMPANY,
      resourceId: companyId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  // Project permissions
  // PERFORMANCE: Use cached hasPermission() instead of calling service directly
  const canCreateProject = useCallback(async (): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.CREATE,
      resource: PermissionResource.PROJECT
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canViewProject = useCallback(async (projectId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.VIEW,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canEditProject = useCallback(async (projectId: string, projectStatus?: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.EDIT,
      resource: PermissionResource.PROJECT,
      resourceId: projectId,
      ...(projectStatus && { context: { projectStatus } })
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canDeleteProject = useCallback(async (projectId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.DELETE,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  // Project-specific operations
  // PERFORMANCE: Use cached hasPermission() instead of calling service directly
  const canCreateSubproject = useCallback(async (projectId: string, projectStatus?: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.CREATE,
      resource: PermissionResource.SUBPROJECT,
      resourceId: projectId,
      ...(projectStatus && { context: { projectStatus } })
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canCreateProjectReport = useCallback(async (projectId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.CREATE,
      resource: PermissionResource.REPORT,
      resourceId: projectId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canAcceptProject = useCallback(async (projectId: string): Promise<boolean> => {
    if (!userContext) return false;
    // For accept/reject, we still need to call service for complex logic
    // (status checks, performing company validation, etc.)
    return await permissionService.canAcceptProject(userContext, projectId);
  }, [userContext]);

  const canRejectProject = useCallback(async (projectId: string): Promise<boolean> => {
    if (!userContext) return false;
    // For accept/reject, we still need to call service for complex logic
    return await permissionService.canRejectProject(userContext, projectId);
  }, [userContext]);

  const canChangeProjectStatus = useCallback(async (projectId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.EDIT,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  // User permissions
  // PERFORMANCE: Use cached hasPermission() instead of calling service directly
  const canCreateUser = useCallback(async (targetCompanyId?: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.CREATE,
      resource: PermissionResource.USER,
      ...(targetCompanyId && { companyScope: targetCompanyId })
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canEditUser = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.EDIT,
      resource: PermissionResource.USER,
      resourceId: targetUserId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  const canDeleteUser = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!userContext) return false;
    const result = await hasPermission({
      action: PermissionAction.DELETE,
      resource: PermissionResource.USER,
      resourceId: targetUserId
    });
    return result.allowed;
  }, [userContext, hasPermission]);

  // ============================================================================
  // USER INFO GETTERS
  // ============================================================================

  const userRole = userContext ? permissionService.getUserRole(userContext) : UserRole.USER;
  const isSystemAdmin = userContext ? permissionService.isSystemAdmin(userContext) : false;
  const isCompanyAdmin = userContext ? permissionService.isCompanyAdmin(userContext) : false;

  // ============================================================================
  // PROJECT MEMBERSHIP HELPERS
  // ============================================================================

  const getUserProjectPermission = useCallback((projectId: string): ProjectPermission | null => {
    // Check if Company Admin (automatic Editor permissions)
    if (userRole === UserRole.COMPANY_ADMIN) {
      return ProjectPermission.EDITOR;
    }

    // Check explicit membership
    const membership = projectMemberships.find(m => m.assignment_id === projectId);
    return membership ? membership.permission_level : null;
  }, [projectMemberships, userRole]);

  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================

  const clearCache = useCallback(() => {
    if (userContext) {
      permissionService.clearUserCache(userContext.id);
    }
  }, [userContext]);

  const refreshPermissions = useCallback(async () => {
    if (!userContext?.id) return;

    // Clear permission cache
    clearCache();

    // Invalidate React Query cache to trigger refetch
    await queryClient.invalidateQueries({ queryKey: ['user-permissions', userContext.id] });
  }, [userContext?.id, clearCache, queryClient]);

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  useEffect(() => {
    if (!userContext?.id) return;

    // Subscribe to project membership changes
    const membershipSubscription = supabase
      .channel('project_membership_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_membership',
          filter: `user_id=eq.${userContext.id}`
        },
        (_payload) => {
          // Project membership changed - invalidate query to refetch
          clearCache();
          queryClient.invalidateQueries({
            queryKey: ['user-permissions', userContext.id],
            exact: true  // PERFORMANCE: Prevent cascade refetches
          });
        }
      )
      .subscribe();

    // Subscribe to user profile changes (role changes)
    const profileSubscription = supabase
      .channel('profile_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userContext.id}`
        },
        (_payload) => {
          // User profile changed - cache invalidated
          clearCache();
          // Trigger a refresh of user context
          refreshPermissions();
        }
      )
      .subscribe();

    return () => {
      membershipSubscription.unsubscribe();
      profileSubscription.unsubscribe();
    };
  }, [userContext?.id, queryClient, clearCache, refreshPermissions]);

  // ============================================================================
  // AUTH STATE LISTENER - CLEAR CACHE ON LOGOUT
  // ============================================================================

  useEffect(() => {
    // Listen for auth state changes to clear cache on logout and System Admin sign-in
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // User signed out - clearing all permission caches
        
        // Clear the secure permission cache completely
        securePermissionCache.clear();
        
        // Clear the permission service cache
        if (userContext) {
          permissionService.clearUserCache(userContext.id);
        }

        // Invalidate React Query cache
        queryClient.clear();
      } else if (event === 'SIGNED_IN' && session?.user?.app_metadata?.['is_system_admin']) {
        // System Admin signed in - clearing all permission caches to prevent stale data
        
        // Clear all caches for System Admin to prevent signature mismatch issues
        securePermissionCache.clear();
        permissionService.clearUserCache(session.user.id);
        
        // Also clear the security key to force regeneration
        permissionIntegrity.clearSecurityKey();
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [userContext, queryClient]);

  // INITIALIZATION: React Query handles loading automatically via useQuery above

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const contextValue: PermissionContextType = {
    // Core permission checking
    hasPermission,
    
    // Convenience methods
    canViewCompany,
    canEditCompany,
    canDeleteCompany,
    canCreateProject,
    canViewProject,
    canEditProject,
    canDeleteProject,
    canCreateSubproject,
    canCreateProjectReport,
    canAcceptProject,
    canRejectProject,
    canChangeProjectStatus,
    canCreateUser,
    canEditUser,
    canDeleteUser,
    
    // User info
    userId: userContext?.id || null,
    userRole,
    isSystemAdmin,
    isCompanyAdmin,
    
    // Project memberships
    projectMemberships,
    getUserProjectPermission,
    
    // Cache management
    clearCache,
    refreshPermissions,
    
    // State
    isLoading,
    
    // Debug mode
    debugMode: debugModeState,
    setDebugMode: setDebugModeState
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// ============================================================================
// DEBUG COMPONENT
// ============================================================================

interface PermissionDebugPanelProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const PermissionDebugPanel: React.FC<PermissionDebugPanelProps> = ({
  position = 'top-right'
}) => {
  const {
    userRole,
    isSystemAdmin,
    isCompanyAdmin,
    projectMemberships,
    debugMode,
    setDebugMode,
    clearCache,
    refreshPermissions
  } = usePermissions();

  const userContext = useUserContext();

  if (!debugMode) return null;

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-50 bg-gray-900 text-white text-xs p-3 rounded-lg shadow-lg max-w-sm`}>
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold">🔐 Permissions Debug</span>
        <button
          onClick={() => setDebugMode(false)}
          className="text-gray-400 hover:text-white"
        >
          ×
        </button>
      </div>
      
      <div className="space-y-1">
        <div><strong>User:</strong> {userContext?.email}</div>
        <div><strong>Role:</strong> {userRole}</div>
        <div><strong>Company:</strong> {userContext?.company_id || 'None'}</div>
        <div><strong>System Admin:</strong> {isSystemAdmin ? '✅' : '❌'}</div>
        <div><strong>Company Admin:</strong> {isCompanyAdmin ? '✅' : '❌'}</div>
        <div><strong>Project Memberships:</strong> {projectMemberships.length}</div>
      </div>
      
      <div className="mt-2 space-y-1">
        <button
          onClick={clearCache}
          className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-xs w-full"
        >
          Clear Cache
        </button>
        <button
          onClick={refreshPermissions}
          className="bg-green-600 hover:bg-green-700 px-2 py-1 rounded text-xs w-full"
        >
          Refresh Permissions
        </button>
      </div>
    </div>
  );
};